import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useChartStore } from '@/store/chartStore';
import { useSymbols } from '@/hooks/useSymbols';
import { Z_INDEX } from '@/constants/zIndex';
import { api } from '@/services/api';
import { isIndexSymbol } from '@/utils/symbol';
import type { FundamentalsResponse, ValidQuarter } from '@/components/Fundamentals/fundamentals.types';
import {
  getValidQuarters,
  getT4QEPS,
  getT4QRevenue,
  fmt,
  pctChangeLabel,
} from '@/components/Fundamentals/fundamentals.utils';
import aelData from '@/components/Fundamentals/ael_data.json';

// ─── Dev fallback ────────────────────────────────────────────────────────────

const DEV_FALLBACK: FundamentalsResponse = {
  symbol:      'AEL',
  companyName: 'Access Engineering PLC',
  exchange:    'CSE · Construction',
  mktPrice:    77.5,
  data:        aelData as FundamentalsResponse['data'],
  forwardData: null,
};

// ─── IBD colour thresholds ───────────────────────────────────────────────────

function growthClass(pct: number | null): string {
  if (pct === null) return 'text-[color:var(--color-muted)]';
  if (pct >= 25)   return 'text-[color:var(--color-up)] font-semibold';
  if (pct > 0)     return 'text-yellow-400';
  return 'text-[color:var(--color-down)]';
}

function growthLabel(pct: number | null): string {
  if (pct === null) return '—';
  return pctChangeLabel(pct);
}

// ─── Revenue formatter: compact for table ────────────────────────────────────

function fmtRev(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1_000_000_000_000) return `${(v / 1_000_000_000_000).toFixed(2)}T`;
  if (v >= 1_000_000_000)     return `${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000)         return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)             return `${(v / 1_000).toFixed(2)}K`;
  return `${v.toFixed(2)}`;
}

// ─── Build quarterly rows (latest first, up to 8) ────────────────────────────

interface QRow {
  label: string;
  eps: number | null;
  epsPct: number | null;
  rev: number | null;
  revPct: number | null;
}

function buildQRows(quarters: ValidQuarter[]): QRow[] {
  const rows: QRow[] = [];
  const reversed = [...quarters].reverse();
  const limit = Math.min(reversed.length, 8);
  for (let i = 0; i < limit; i++) {
    const q = reversed[i];
    const prior = quarters.find(p => p.month === q.month && p.year === q.year - 1);
    const eps = q.data.INCOME_STATEMENT.EPS;
    const priorEps = prior?.data.INCOME_STATEMENT.EPS ?? null;
    const epsPct = eps !== null && priorEps !== null && priorEps !== 0
      ? ((eps - priorEps) / Math.abs(priorEps)) * 100 : null;

    const rev = q.data.INCOME_STATEMENT.REVENUE;
    const priorRev = prior?.data.INCOME_STATEMENT.REVENUE ?? null;
    const revPct = rev !== null && priorRev !== null && priorRev !== 0
      ? ((rev - priorRev) / Math.abs(priorRev)) * 100 : null;

    rows.push({ label: q.label.replace(' 20', " '"), eps, epsPct, rev, revPct });
  }
  return rows;
}

// ─── Build annual rows ────────────────────────────────────────────────────────

interface AnnualRow {
  label: string;
  eps: number | null;
  epsPct: number | null;
  rev: number | null;
  revPct: number | null;
}

const MONTHS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;

function sumYearEPS(quarters: ValidQuarter[], year: number): number | null {
  let sum = 0;
  for (const m of MONTHS) {
    const q = quarters.find(x => x.month === m && x.year === year);
    if (!q) return null;
    const v = q.data.INCOME_STATEMENT.EPS;
    if (v === null) return null;
    sum += v;
  }
  return sum;
}

function sumYearRev(quarters: ValidQuarter[], year: number): number | null {
  let sum = 0;
  for (const m of MONTHS) {
    const q = quarters.find(x => x.month === m && x.year === year);
    if (!q) return null;
    const v = q.data.INCOME_STATEMENT.REVENUE;
    if (v === null) return null;
    sum += v;
  }
  return sum;
}

function buildAnnualRows(quarters: ValidQuarter[]): AnnualRow[] {
  if (quarters.length === 0) return [];
  const n = quarters.length;

  // TTM from last 4 quarters
  const ttmEPS = n >= 4 ? getT4QEPS(quarters, n - 1) : null;
  const ttmRev = n >= 4 ? getT4QRevenue(quarters, n - 1) : null;
  const ttmYear = quarters[n - 1].year;

  // Prior TTM for YoY on TTM row
  const priorTtmEPS = n >= 8 ? getT4QEPS(quarters, n - 5) : null;
  const priorTtmRev = n >= 8 ? getT4QRevenue(quarters, n - 5) : null;
  const ttmEpsPct = ttmEPS !== null && priorTtmEPS !== null && priorTtmEPS !== 0
    ? ((ttmEPS - priorTtmEPS) / Math.abs(priorTtmEPS)) * 100 : null;
  const ttmRevPct = ttmRev !== null && priorTtmRev !== null && priorTtmRev !== 0
    ? ((ttmRev - priorTtmRev) / Math.abs(priorTtmRev)) * 100 : null;

  // 3 most recent full calendar years
  const fullYear = ttmYear - 1;
  const years = [fullYear, fullYear - 1, fullYear - 2];

  const rows: AnnualRow[] = [];

  // ── Helpers for 3-year average annual YoY growth ─────────────────────
  function avgAnnualGrowth(
    getSumFn: (yr: number) => number | null,
    baseYear: number,  // most recent full year
  ): number | null {
    const rates: number[] = [];
    for (let i = 0; i < 3; i++) {
      const yr = baseYear - i;
      const cur = getSumFn(yr);
      const prior = getSumFn(yr - 1);
      if (cur !== null && prior !== null && prior !== 0)
        rates.push(((cur - prior) / Math.abs(prior)) * 100);
    }
    return rates.length > 0 ? rates.reduce((a, b) => a + b, 0) / rates.length : null;
  }

  // ── FY Avg: sum of available quarters × (4/n); fallback to TTM ───────
  {
    const fwdYear = ttmYear;
    const releasedQs = MONTHS
      .map(m => quarters.find(x => x.month === m && x.year === fwdYear))
      .filter((q): q is ValidQuarter => q !== undefined);

    let avgEPS: number | null;
    let avgRev: number | null;
    let priorEPS: number | null;
    let priorRev: number | null;

    if (releasedQs.length > 0) {
      const epsVals = releasedQs.map(q => q.data.INCOME_STATEMENT.EPS).filter((v): v is number => v !== null);
      const revVals = releasedQs.map(q => q.data.INCOME_STATEMENT.REVENUE).filter((v): v is number => v !== null);
      const n = releasedQs.length;
      avgEPS = epsVals.length === n ? epsVals.reduce((a, b) => a + b, 0) * (4 / n) : null;
      avgRev = revVals.length === n ? revVals.reduce((a, b) => a + b, 0) * (4 / n) : null;
      priorEPS = sumYearEPS(quarters, fwdYear - 1);
      priorRev = sumYearRev(quarters, fwdYear - 1);
    } else {
      // No quarters yet: use TTM as estimate
      avgEPS = ttmEPS;
      avgRev = ttmRev;
      priorEPS = sumYearEPS(quarters, fwdYear - 1);
      priorRev = sumYearRev(quarters, fwdYear - 1);
    }

    const epsPct = avgEPS !== null && priorEPS !== null && priorEPS !== 0
      ? ((avgEPS - priorEPS) / Math.abs(priorEPS)) * 100 : null;
    const revPct = avgRev !== null && priorRev !== null && priorRev !== 0
      ? ((avgRev - priorRev) / Math.abs(priorRev)) * 100 : null;

    rows.push({
      label: `FY${String(fwdYear).slice(-2)}AE`,
      eps: avgEPS,
      epsPct,
      rev: avgRev,
      revPct,
    });
  }

  // ── FY Grow: avg YoY growth → project missing quarters → sum ─────────
  // Fallback (no quarters released): apply 3-yr avg annual growth to prior year totals
  {
    const fwdYear = ttmYear;
    const fullYear = fwdYear - 1; // prior full year
    const releasedQs = MONTHS
      .map(m => quarters.find(x => x.month === m && x.year === fwdYear))
      .filter((q): q is ValidQuarter => q !== undefined);

    let growEPS: number | null = null;
    let growRev: number | null = null;
      const priorEPS: number | null = sumYearEPS(quarters, fullYear);
      const priorRev: number | null = sumYearRev(quarters, fullYear);

    if (releasedQs.length > 0) {
      // Compute per-quarter YoY growth rates from released quarters
      const epsGrowthRates: number[] = [];
      const revGrowthRates: number[] = [];

      for (const q of releasedQs) {
        const prior = quarters.find(x => x.month === q.month && x.year === fwdYear - 1);
        if (!prior) continue;
        const eps = q.data.INCOME_STATEMENT.EPS;
        const priorEps = prior.data.INCOME_STATEMENT.EPS;
        if (eps !== null && priorEps !== null && priorEps !== 0)
          epsGrowthRates.push(((eps - priorEps) / Math.abs(priorEps)) * 100);

        const rev = q.data.INCOME_STATEMENT.REVENUE;
        const priorRev = prior.data.INCOME_STATEMENT.REVENUE;
        if (rev !== null && priorRev !== null && priorRev !== 0)
          revGrowthRates.push(((rev - priorRev) / Math.abs(priorRev)) * 100);
      }

      const avgEpsGrowth = epsGrowthRates.length > 0
        ? epsGrowthRates.reduce((a, b) => a + b, 0) / epsGrowthRates.length : null;
      const avgRevGrowth = revGrowthRates.length > 0
        ? revGrowthRates.reduce((a, b) => a + b, 0) / revGrowthRates.length : null;

      // Sum released actuals
      const releasedEPSSum = releasedQs
        .map(q => q.data.INCOME_STATEMENT.EPS)
        .filter((v): v is number => v !== null)
        .reduce((a, b) => a + b, 0);
      const releasedRevSum = releasedQs
        .map(q => q.data.INCOME_STATEMENT.REVENUE)
        .filter((v): v is number => v !== null)
        .reduce((a, b) => a + b, 0);

      const hasEnoughEPS = releasedQs.every(q => q.data.INCOME_STATEMENT.EPS !== null);
      const hasEnoughRev = releasedQs.every(q => q.data.INCOME_STATEMENT.REVENUE !== null);

      // If full-year data is available, project next-year totals using avg YoY growth
      if (releasedQs.length === 4) {
        growEPS = avgEpsGrowth !== null && hasEnoughEPS
          ? releasedEPSSum * (1 + avgEpsGrowth / 100)
          : null;
        growRev = avgRevGrowth !== null && hasEnoughRev
          ? releasedRevSum * (1 + avgRevGrowth / 100)
          : null;
      }

      if (releasedQs.length < 4) {
        // Project missing quarters
        const releasedMonths = new Set(releasedQs.map(q => q.month));
        const missingMonths = MONTHS.filter(m => !releasedMonths.has(m));
        let projectedEPSSum = 0;
        let projectedRevSum = 0;
        let epsProjectable = avgEpsGrowth !== null;
        let revProjectable = avgRevGrowth !== null;

        for (const m of missingMonths) {
          const priorQ = quarters.find(x => x.month === m && x.year === fwdYear - 1);
          if (!priorQ) { epsProjectable = false; revProjectable = false; continue; }
          if (epsProjectable) {
            const pv = priorQ.data.INCOME_STATEMENT.EPS;
            if (pv !== null) projectedEPSSum += pv * (1 + avgEpsGrowth! / 100);
            else epsProjectable = false;
          }
          if (revProjectable) {
            const pv = priorQ.data.INCOME_STATEMENT.REVENUE;
            if (pv !== null) projectedRevSum += pv * (1 + avgRevGrowth! / 100);
            else revProjectable = false;
          }
        }

        growEPS = epsProjectable && hasEnoughEPS ? releasedEPSSum + projectedEPSSum : null;
        growRev = revProjectable && hasEnoughRev ? releasedRevSum + projectedRevSum : null;
      }
    } else {
      // No quarters released: apply 3-yr avg annual growth to prior full year
      const avgEpsGrowth = avgAnnualGrowth(yr => sumYearEPS(quarters, yr), fullYear);
      const avgRevGrowth = avgAnnualGrowth(yr => sumYearRev(quarters, yr), fullYear);
      growEPS = priorEPS !== null && avgEpsGrowth !== null
        ? priorEPS * (1 + avgEpsGrowth / 100) : null;
      growRev = priorRev !== null && avgRevGrowth !== null
        ? priorRev * (1 + avgRevGrowth / 100) : null;
    }

    const epsPct = growEPS !== null && priorEPS !== null && priorEPS !== 0
      ? ((growEPS - priorEPS) / Math.abs(priorEPS)) * 100 : null;
    const revPct = growRev !== null && priorRev !== null && priorRev !== 0
      ? ((growRev - priorRev) / Math.abs(priorRev)) * 100 : null;

    rows.push({
      label: `FY${String(fwdYear).slice(-2)}GE`,
      eps: growEPS,
      epsPct,
      rev: growRev,
      revPct,
    });
  }

  // ── TTM ────────────────────────────────────────────────────────────────
  rows.push({
    label: `FY${String(ttmYear).slice(-2)} TTM`,
    eps: ttmEPS,
    epsPct: ttmEpsPct,
    rev: ttmRev,
    revPct: ttmRevPct,
  });

  // ── Historical full years ─────────────────────────────────────────────
  for (const yr of years) {
    const yEPS = sumYearEPS(quarters, yr);
    const priorYEPS = sumYearEPS(quarters, yr - 1);
    const yRev = sumYearRev(quarters, yr);
    const priorYRev = sumYearRev(quarters, yr - 1);
    const epsPct = yEPS !== null && priorYEPS !== null && priorYEPS !== 0
      ? ((yEPS - priorYEPS) / Math.abs(priorYEPS)) * 100 : null;
    const revPct = yRev !== null && priorYRev !== null && priorYRev !== 0
      ? ((yRev - priorYRev) / Math.abs(priorYRev)) * 100 : null;
    rows.push({
      label: `FY${String(yr).slice(-2)}`,
      eps: yEPS,
      epsPct,
      rev: yRev,
      revPct,
    });
  }

  return rows;
}

// ─── Drag icon (bar-chart) ────────────────────────────────────────────────────

function GrowthBarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2"  y="14" width="4" height="8" rx="1"/>
      <rect x="9"  y="9"  width="4" height="13" rx="1"/>
      <rect x="16" y="4"  width="4" height="18" rx="1"/>
      <line x1="2" y1="3" x2="22" y2="3" strokeDasharray="2 2"/>
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  onOpenFullFinancials: () => void;
}

export function IBDGrowthWidget({ onOpenFullFinancials }: Props) {
  const symbol = useChartStore((s) => s.symbol);
  const { symbols } = useSymbols();

  // Position (starts on price pane, right of the left toolbar)
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    if (typeof window === 'undefined') return { x: 72, y: 122 };
    try {
      const raw = window.localStorage.getItem('fundamentalsWidgetPos');
      if (raw) {
        const parsed = JSON.parse(raw) as { x?: number; y?: number };
        if (typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          return { x: parsed.x, y: parsed.y };
        }
      }
    } catch {
      /* ignore */
    }
    return { x: 72, y: 122 };
  });
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const raw = window.localStorage.getItem('fundamentalsWidgetOpen');
      return raw === null ? true : raw === 'true';
    } catch {
      return true;
    }
  });

  // Data
  const [data, setData] = useState<FundamentalsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedFor = useRef<string | null>(null);

  const isIndex = useMemo(() => {
    if (isIndexSymbol(symbol)) return true;
    const info = symbols.find((s) => s.symbol === symbol);
    return info?.type === 'index';
  }, [symbols, symbol]);

  // Drag state
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const pointerDownPos = useRef({ x: 0, y: 0 });
  const didDrag = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Fetch when widget opens or symbol changes while open
  const fetchData = useCallback((sym: string) => {
    if (fetchedFor.current === sym) return;
    setLoading(true);
    setData(null);

    if (import.meta.env.DEV) {
      setTimeout(() => {
        setData(DEV_FALLBACK);
        setLoading(false);
        fetchedFor.current = sym;
      }, 0);
      return;
    }

    api.fundamentals(sym, ['earnings_per_share_basic', 'total_revenue'])
      .then((res) => {
        setData(res);
        fetchedFor.current = sym;
      })
      .catch(() => {
        setData(DEV_FALLBACK);
        fetchedFor.current = sym;
      })
      .finally(() => setLoading(false));
  }, []);

  // Refetch when symbol changes while open
  useEffect(() => {
    if (isIndex) {
      setData(null);
      setLoading(false);
      fetchedFor.current = null;
      if (open) setOpen(false);
      return;
    }
    if (open) fetchData(symbol);
  }, [open, symbol, fetchData, isIndex]);

  useEffect(() => {
    try {
      window.localStorage.setItem('fundamentalsWidgetPos', JSON.stringify(pos));
    } catch {
      /* ignore */
    }
  }, [pos]);

  useEffect(() => {
    try {
      window.localStorage.setItem('fundamentalsWidgetOpen', String(open));
    } catch {
      /* ignore */
    }
  }, [open]);

  // ── Drag logic ───────────────────────────────────────────────────────────

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Only main mouse button or touch
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragging.current = true;
    didDrag.current = false;
    pointerDownPos.current = { x: e.clientX, y: e.clientY };
    dragOffset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dx = Math.abs(e.clientX - pointerDownPos.current.x);
    const dy = Math.abs(e.clientY - pointerDownPos.current.y);
    if (dx > 4 || dy > 4) didDrag.current = true;

    const panelW = panelRef.current?.offsetWidth  ?? 48;
    const panelH = panelRef.current?.offsetHeight ?? 48;
    const newX = Math.max(0, Math.min(window.innerWidth  - panelW, e.clientX - dragOffset.current.x));
    const newY = Math.max(0, Math.min(window.innerHeight - panelH, e.clientY - dragOffset.current.y));
    setPos({ x: newX, y: newY });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    dragging.current = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    // If barely moved, treat as click → toggle
    if (!didDrag.current) {
      setOpen(o => !o);
    }
  }, []);

  // ── Derived data ─────────────────────────────────────────────────────────

  const quarters = useMemo((): ValidQuarter[] => {
    if (!data || Object.keys(data.data).length === 0) return [];
    return getValidQuarters(data.data);
  }, [data]);

  const qRows     = useMemo(() => buildQRows(quarters),                        [quarters]);
  const annualRows = useMemo(() => buildAnnualRows(quarters), [quarters]);

  const companyShort = data
    ? (data.companyName.length > 28 ? data.companyName.slice(0, 26) + '…' : data.companyName)
    : symbol;

  // ── Table cell helpers ───────────────────────────────────────────────────

  function EpsCell({ v }: { v: number | null }) {
    return <td className="px-2 py-0.5 text-right tabular-nums text-[color:var(--color-text)]">{v !== null ? fmt(v) : '—'}</td>;
  }

  function RevCell({ v }: { v: number | null }) {
    return <td className="px-2 py-0.5 text-right tabular-nums text-[color:var(--color-text)]">{fmtRev(v)}</td>;
  }

  function PctCell({ pct }: { pct: number | null }) {
    return <td className={`px-2 py-0.5 text-right tabular-nums ${growthClass(pct)}`}>{growthLabel(pct)}</td>;
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      ref={panelRef}
      data-fundamentals-widget
      style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: Z_INDEX.dropdown }}
      className="select-none"
    >
      {/* Header / collapsed icon */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={`
          flex items-center gap-2 cursor-grab active:cursor-grabbing touch-none
          rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-panel)]
          shadow-lg transition-colors
          ${open ? 'rounded-b-none border-b-0 px-3 py-2' : 'px-3 py-2'}
        `}
        title={
          open
            ? 'Drag to move · Click to collapse'
            : isIndex
              ? 'Index · Fundamentals unavailable'
              : 'Fundamentals · Click to expand'
        }
      >
        <span className="text-[color:var(--color-accent)] shrink-0">
          <GrowthBarIcon />
        </span>

        {open && (
          <>
            <div className="flex flex-col leading-tight min-w-0">
              <span data-fundamentals-header-title className="text-[13px] font-semibold text-[color:var(--color-text)] truncate leading-tight">
                {companyShort}
              </span>
              <span data-fundamentals-header-subtitle className="text-[11px] text-[color:var(--color-muted)] leading-tight">
                {isIndex ? 'Index · Fundamentals unavailable' : 'Fundamentals · EPS & Revenue'}
              </span>
            </div>
            <button
              onPointerDown={e => e.stopPropagation()}
              onClick={() => setOpen(false)}
              className="ml-auto text-[color:var(--color-muted)] hover:text-[color:var(--color-text)] transition-colors shrink-0"
              title="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </>
        )}

        {!open && isIndex && (
          <span className="ml-2 text-[12px] text-[color:var(--color-muted)]">Index</span>
        )}
      </div>

      {/* Expanded panel */}
      {open && (
        <div data-fundamentals-panel className="rounded-b-lg border border-[color:var(--color-border)] bg-[color:var(--color-panel)] shadow-lg overflow-hidden w-[392px]">
          {loading ? (
            <div className="flex items-center justify-center h-24 text-[color:var(--color-muted)] text-sm">
              Loading…
            </div>
          ) : quarters.length === 0 ? (
            <div className="flex items-center justify-center h-20 text-[color:var(--color-muted)] text-sm">
              No quarterly data available
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-collapse">
                {/* ── Column header ─────────────────────────────────── */}
                <thead>
                  <tr className="border-b border-[color:var(--color-border)]">
                    <th className="px-2 py-1 text-left text-[11px] uppercase tracking-wide text-[color:var(--color-muted)] font-medium w-[84px]">Quarter</th>
                    <th className="px-2 py-1 text-right text-[11px] uppercase tracking-wide text-[color:var(--color-muted)] font-medium">EPS</th>
                    <th className="px-2 py-1 text-right text-[11px] uppercase tracking-wide text-[color:var(--color-muted)] font-medium">EPS YoY</th>
                    <th className="px-2 py-1 text-right text-[11px] uppercase tracking-wide text-[color:var(--color-muted)] font-medium">Revenue</th>
                    <th className="px-2 py-1 text-right text-[11px] uppercase tracking-wide text-[color:var(--color-muted)] font-medium">Rev YoY</th>
                  </tr>
                </thead>

                <tbody>
                  {/* ── Quarterly rows ────────────────────────────────── */}
                  {qRows.map((r, i) => (
                    <tr
                      key={r.label}
                      className={`
                        border-b border-[color:var(--color-border)]/40
                        ${i === 0 ? 'bg-[color:var(--color-accent)]/5' : 'hover:bg-[color:var(--color-panel-hover)]'}
                        transition-colors
                      `}
                    >
                      <td className="px-2 py-0.5 font-medium text-[color:var(--color-text)]">{r.label}</td>
                      <EpsCell v={r.eps} />
                      <PctCell pct={r.epsPct} />
                      <RevCell v={r.rev} />
                      <PctCell pct={r.revPct} />
                    </tr>
                  ))}

                  {/* ── Divider ──────────────────────────────────────── */}
                  <tr>
                    <td colSpan={5} className="py-0">
                      <div className="border-t-2 border-[color:var(--color-border)]" />
                    </td>
                  </tr>

                  {/* ── Annual rows ───────────────────────────────────── */}
                  {annualRows.map(r => {
                    const isFwd = r.label.endsWith('E') || r.label.endsWith('AE') || r.label.endsWith('GE');
                    return (
                      <tr
                        key={r.label}
                        className={`border-b border-[color:var(--color-border)]/40 hover:bg-[color:var(--color-panel-hover)] transition-colors ${isFwd ? 'opacity-80' : ''}`}
                      >
                        <td className={`px-2 py-0.5 font-semibold ${isFwd ? 'italic text-[color:var(--color-accent)]' : 'text-[color:var(--color-muted)]'}`}>
                          {r.label}
                        </td>
                        <EpsCell v={r.eps} />
                        <PctCell pct={r.epsPct} />
                        <RevCell v={r.rev} />
                        <PctCell pct={r.revPct} />
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* ── Legend ─────────────────────────────────────────────── */}
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 px-3 py-1.5 border-t border-[color:var(--color-border)]/40">
                <span className="flex items-center gap-1 text-[11px]">
                  <span className="w-2 h-2 rounded-sm bg-[color:var(--color-up)] inline-block" />
                  <span className="text-[color:var(--color-muted)]">≥25%</span>
                </span>
                <span className="flex items-center gap-1 text-[11px]">
                  <span className="w-2 h-2 rounded-sm bg-yellow-400 inline-block" />
                  <span className="text-[color:var(--color-muted)]">1–24%</span>
                </span>
                <span className="flex items-center gap-1 text-[11px]">
                  <span className="w-2 h-2 rounded-sm bg-[color:var(--color-down)] inline-block" />
                  <span className="text-[color:var(--color-muted)]">&lt;0%</span>
                </span>
                <span className="ml-auto text-[11px] italic text-[color:var(--color-accent)]">AE: Avg Est. &nbsp;·&nbsp; GE: Growth Est.</span>
              </div>

              {!isIndex && (
                <button
                  onPointerDown={e => e.stopPropagation()}
                  onClick={onOpenFullFinancials}
                  className="w-full flex items-center justify-center gap-1 px-3 py-2 text-base font-semibold text-[color:var(--color-accent)] border-t border-[color:var(--color-border)]/40 hover:bg-[color:var(--color-panel-hover)] transition-colors"
                >
                  Full Financials <span aria-hidden="true">→</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
